import type { Choices } from "@busbud/int-schemas/lib";
import type * as UDM from "@busbud/int-schemas/udm";

import { UDMGender } from "@app/constants/passengers";

interface GenderList {
  female: string[];
  male: string[];
}

export type SimplifiedBusLayout = Pick<UDM.Bus, "layout" | "rules">;

export class SeatSelectionGenderRules {
  // An array of adjacent seats, for example the following bus layout:
  //    FRONT
  //  1 - - 2 3
  //  4 - - 5 6
  //    BACK
  // Will be break into 4 groups : [[1], [2, 3], [4], [5, 6]]
  private adjacent_seats: string[][] = [];

  // Allow us to access seats by id instead of parsing the bus layout
  private seats_by_id: Record<string, UDM.SeatCell> = {};

  private gendered_seats_rules: { female: number; male: number };
  private gendered_seats_before_selection: GenderList;
  private gendered_seats_after_selection: GenderList;

  public constructor(bus: SimplifiedBusLayout) {
    const layout = bus.layout;
    layout!.forEach(floor => {
      floor.cells.forEach(row => {
        let grouped_seats: string[] = [];

        row.forEach((cell: UDM.Cell) => {
          if (cell.type === "seat") {
            grouped_seats.push(cell.id);
            this.seats_by_id[cell.id] = cell;
          } else if (grouped_seats.length > 0) {
            this.adjacent_seats.push(grouped_seats);
            grouped_seats = [];
          }
        });

        if (grouped_seats.length > 0) {
          this.adjacent_seats.push(grouped_seats);
        }
      });
    });

    this.gendered_seats_rules = {
      female: this.getMaxGenderRule(bus, UDMGender.Female),
      male: this.getMaxGenderRule(bus, UDMGender.Male)
    };

    // `gendered_seats` refers to the empty, available seats that only accept a specific gender
    this.gendered_seats_before_selection = this.getAvailableSeatIdsByGender();
    this.gendered_seats_after_selection = this.getEmptyGenderLists();
  }

  public validateRules(selected_seats: {
    [id: string]: Choices.Gender;
  }): boolean {
    this.gendered_seats_after_selection =
      this.getAvailableSeatIdsByGender(selected_seats);

    return (
      this.isGenderRuleValid(UDMGender.Female) &&
      this.isGenderRuleValid(UDMGender.Male)
    );
  }

  public getInvalidGender(): Choices.Gender | null {
    if (!this.gendered_seats_after_selection) {
      return null;
    }

    return this.isGenderRuleValid(UDMGender.Female)
      ? UDMGender.Male
      : UDMGender.Female;
  }

  private isGenderRuleValid(gender: Choices.Gender): boolean {
    // 1) selection is valid if there are less than N available gendered seats
    const is_rule_respected =
      this.gendered_seats_after_selection[gender].length <=
      this.gendered_seats_rules[gender];

    // 2) After reverse-engineering Biletall's rules implementation, we found out that selection is
    //  valid if there are more than N available gendered seats BUT we didn't create any new gendered
    //  seats with our current selection (available seats are the same as they were before selection).
    //  (N being the number of maximum gendered seat defined in the rules)
    const is_rule_violation_improved = this.gendered_seats_after_selection[
      gender
    ].every(seat_id =>
      this.gendered_seats_before_selection[gender].includes(seat_id)
    );

    return is_rule_respected || is_rule_violation_improved;
  }

  private getAvailableSeatIdsByGender(
    selected_seats: { [id: string]: Choices.Gender } = {}
  ): GenderList {
    const result = this.getEmptyGenderLists();

    this.adjacent_seats.reduce<GenderList>(
      (acc, group) => {
        if (!this.isTestableGroup(group, selected_seats)) {
          return acc;
        }

        group.forEach((seat_id, index) => {
          // If seat isn't occuppied/selected, let's guess its future gender restriction
          if (this.isFree(seat_id, selected_seats)) {
            const next_seat_id = index === 0 ? group[1] : group[0];
            const next_seat_gender = this.getGender(
              // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
              next_seat_id,
              selected_seats
            );

            if ([UDMGender.Female, UDMGender.Male].includes(next_seat_gender)) {
              result[next_seat_gender].push(seat_id);
            }
          }
        });

        return acc;
      },
      { male: [], female: [] }
    );

    return result;
  }

  private getEmptyGenderLists(): GenderList {
    return {
      female: [],
      male: []
    };
  }

  private isFree(
    seat_id: string,
    selected_seats: { [id: string]: Choices.Gender }
  ): boolean {
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    return this.seats_by_id[seat_id].availability && !selected_seats[seat_id];
  }

  private isTestableGroup(
    group: string[],
    selected_seats: { [id: string]: Choices.Gender }
  ): boolean {
    // must be a group of 2 seats
    // must have one occupied and one available seats
    return (
      group.length === 2 &&
      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      this.isFree(group[0], selected_seats) !==
        // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        this.isFree(group[1], selected_seats)
    );
  }

  private getGender(
    seat_id: string,
    selected_seats: { [id: string]: Choices.Gender }
  ): Choices.Gender {
    // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    return selected_seats[seat_id] || this.seats_by_id[seat_id].gender;
  }

  public getMaxGenderRule(
    bus: SimplifiedBusLayout,
    gender: Choices.Gender
  ): number {
    const { rules } = bus;

    return (
      (rules &&
        rules.max_number_of_gendered_seat &&
        rules.max_number_of_gendered_seat[gender]) ||
      Infinity
    );
  }

  public getGenderedSeatBeforeSelection(
    gender: Choices.Gender
  ): UDM.SeatCell["id"][] {
    return this.gendered_seats_before_selection[gender];
  }
}
